import { autocomplete } from "@algolia/autocomplete-js";
import { createQuerySuggestionsPlugin } from "@algolia/autocomplete-plugin-query-suggestions";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import { history } from "instantsearch.js/es/lib/routers";
import { simple } from "instantsearch.js/es/lib/stateMappings";
import {
    configure,
    currentRefinements,
    dynamicWidgets,
    infiniteHits,
    panel,
    rangeSlider,
    refinementList,
    searchBox,
    stats,
} from "instantsearch.js/es/widgets";
import aa from "search-insights";
import googleAnalyticsMiddleware from "../../instantsearch/middlewares/googleAnalyticsMiddleware";
import advancedToggleRefinement from "../../instantsearch/widgets/advancedToggleRefinement";
import mapboxGeoSearchWidget from "../../instantsearch/widgets/mapboxGeoSearchWidget";
import nonLinearRangeSlider from "../../instantsearch/widgets/nonLinearRangeSlider";

function get(obj, path, defaultValue) {
    const keys = path.split(".");

    const result = keys.reduce((acc, key) => {
        if (acc === null || acc === undefined) {
            return acc;
        }
        return acc[key];
    }, obj);

    return result === undefined ? defaultValue : result;
}

export default function (
    options = {},
    { init, destroy, translations, forex, locale, onStateChange, ...props },
) {
    let _instantsearch;
    let _client;
    let _autocomplete;

    let _widgets = {};

    let _routing = {
        history: history({
            cleanUrlOnDispose: true,
        }),
        stateMapping: simple(),
    };

    return {
        loading: true,
        translations: translations,
        forex: forex,
        locale: locale,
        ...props,
        instantsearch() {
            return _instantsearch;
        },
        client() {
            return _client;
        },
        widgets() {
            return _widgets;
        },
        indexName() {
            return options.instantsearch.indexName;
        },
        routeToState() {
            const state = _routing.stateMapping.routeToState(_routing.history.read());

            if (state) {
                return state[this.indexName()] || {};
            }

            return {};
        },
        init() {
            const _this = this;

            _client = algoliasearch(options.algoliasearch.id, options.algoliasearch.secret);

            _instantsearch = instantsearch({
                searchClient: _client,
                routing: _routing,
                insights: true,
                numberLocale: locale || "en",
                stalledSearchDelay: 500,
                future: {
                    preserveSharedStateOnUnmount: true,
                },
                onStateChange: onStateChange?.bind(this),
                ...options.instantsearch,
            });

            if (options.userToken) {
                /**
                 * By default UserToken is attached to the device / IP of the user
                 * and is used for user not authentified
                 *
                 * We set the userToken and AuthenticatedUserToken
                 * because 99% of our users are auth and it simplifies
                 * user Identification at the price of losing unauthentificated
                 * user data
                 */
                aa("setUserToken", options.userToken);
                aa("setAuthenticatedUserToken", options.userToken);
            }

            _instantsearch.on("render", () => {
                if (_instantsearch.status === "stalled") {
                    _this.loading = true;
                } else {
                    _this.loading = false;
                }
            });

            init?.bind(this)();
        },
        dynamicWidgets(options) {
            return dynamicWidgets(options);
        },
        mapboxGeoSearchWidget(options, dispose) {
            return mapboxGeoSearchWidget(options, dispose);
        },
        advancedToggleRefinement(options, panelOptions) {
            return panel({
                cssClasses: {
                    root: "mb-3",
                    header: "font-bold mb-2",
                },
                ...panelOptions,
            })(advancedToggleRefinement)({
                cssClasses: {
                    label: "flex gap-2 items-center hover:bg-gray-200 rounded-md px-1.5 py-0.5 -mx-1.5 transition",
                    radio: "el-radio",
                    count: "bg-gray-200 text-xs px-1.5 py-0.5 rounded-md font-semibold ml-auto",
                },
                ...options,
            });
        },
        refinementList(attribute, header, { templates, cssClasses, ...options }) {
            const _this = this;
            return (container) =>
                panel({
                    cssClasses: {
                        root: "mb-3",
                        header: "font-bold mb-2",
                    },
                    templates: {
                        header: header ? header : ({}, { html }) => html`${attribute}`,
                    },
                })(refinementList)({
                    container,
                    attribute: attribute,
                    sortBy: ["count"],
                    cssClasses: {
                        checkbox: "el-checkbox",
                        label: "flex gap-2 items-center hover:bg-gray-200 rounded-md px-1.5 py-0.5 -mx-1.5 transition",
                        labelText: "truncate",
                        count: "bg-gray-200 text-xs px-1.5 py-0.5 rounded-md font-semibold ml-auto",
                        showMore: [
                            "el-button el-spacing el-text el-bg el-ring el-outline el-border'",
                            "el-size-xs el-color-white",
                            "rounded-md font-semibold ring-1 ring-inset w-full mt-2 justify-center",
                        ],
                        disabledShowMore: "hidden",
                        searchableRoot: "mb-2",
                        searchableForm: "flex gap-1",
                        searchableInput: [
                            "el-input el-text el-bg el-outline el-ring",
                            "el-color-white el-size-sm",
                            "ring-1 ring-inset rounded-md grow",
                        ],
                        searchableReset: "hidden",
                        searchableSubmit: [
                            "hidden",
                            "el-button el-spacing el-text el-bg el-ring el-outline el-border'",
                            "el-size-xs el-color-white",
                            "ring-1 ring-inset rounded-md",
                        ],
                        ...cssClasses,
                    },
                    templates: {
                        showMoreText(data, { html }) {
                            if (data.isShowingMore) {
                                return _this.trans("pagination.less");
                            }
                            return _this.trans("pagination.more");
                        },
                        ...templates,
                    },
                    limit: 5,
                    showMoreLimit: 100,
                    ...options,
                });
        },
        rangeSlider(attribute, header, options) {
            return (container) =>
                panel({
                    cssClasses: {
                        root: "mb-3",
                        header: "font-bold mb-10",
                    },
                    templates: {
                        header: header ? header : ({}, { html }) => html`${attribute}`,
                    },
                })(rangeSlider)({
                    container,
                    attribute: attribute,
                    pips: false,
                    ...options,
                });
        },
        nonLinearRangeSlider(attribute, header, options) {
            return (container) =>
                panel({
                    cssClasses: {
                        root: "mb-3",
                        header: "font-bold mb-10",
                    },
                    templates: {
                        header: header ? header : ({}, { html }) => html`${attribute}`,
                    },
                })(nonLinearRangeSlider)({
                    container,
                    attribute,
                    ...options,
                });
        },
        configure(options) {
            return configure(options);
        },
        stats(options) {
            return stats(options);
        },
        currentRefinements({ cssClasses, ...options }) {
            return currentRefinements({
                cssClasses: {
                    list: "flex flex-wrap gap-3",
                    item: "flex items-center flex-wrap gap-1 text-sm",
                    category: [
                        "pl-2 pr-1 py-1",
                        "bg-white text-black",
                        "font-semibold text-xs",
                        "flex items-center gap-1",
                        "rounded-full ring-1 ring-inset ring-gray-200",
                    ],
                    delete: "bg-gray-200 hover:bg-gray-300 rounded-full text-2xs size-4",
                    ...cssClasses,
                },
                ...options,
            });
        },
        infiniteHits({ cssClasses, ...options }) {
            return infiniteHits({
                showPrevious: true,
                cssClasses: {
                    loadMore: [
                        "el-button el-spacing el-text el-bg el-ring el-outline el-border",
                        "el-color-black el-size-md",
                        `rounded-md font-semibold mx-auto`,
                    ],
                    loadPrevious: [
                        "el-button el-spacing el-text el-bg el-ring el-outline el-border",
                        "el-color-black el-size-md",
                        `rounded-md font-semibold mx-auto`,
                    ],
                    ...cssClasses,
                },

                ...options,
            });
        },
        searchBox(options) {
            return searchBox({
                showSubmit: false,
                showReset: false,
                searchAsYouType: false,
                cssClasses: {
                    form: ["flex gap-1 items-center"],
                    input: ["el-input el-text el-bg el-outline el-ring ", "grow rounded-md"],
                    submit: [
                        "el-button el-spacing el-text el-bg el-ring el-outline el-border",
                        "rounded-md hidden",
                    ],
                },
                ...options,
            });
        },
        destroy() {
            _instantsearch.dispose();

            _autocomplete?.destroy();

            destroy?.bind(this)();
        },
        registerWidget(key, widget) {
            _widgets[key] = { active: false, widget };
        },
        unregisterWidget(key) {
            if (this.hasWidget(key)) {
                delete _widgets[key];
            }
        },
        hasWidget(key) {
            return Object.hasOwn(_widgets, key);
        },
        isWidgetActive(key) {
            return _widgets[key]?.active || false;
        },
        addWidget(key, widget) {
            if (widget) {
                this.registerWidget(key, widget);
                return this.addWidgets([key]);
            }

            return this.addWidgets([key]);
        },
        removeWidget(key) {
            return this.removeWidgets([key]);
        },
        removeWidgets(keys) {
            const items = keys.map((key) => _widgets[key]).filter((item) => item && item.active);

            items.forEach((item) => {
                item.active = false;
            });

            return _instantsearch.removeWidgets(items.map((item) => item.widget));
        },
        addWidgets(keys) {
            const items = keys.map((key) => _widgets[key]).filter((item) => item && !item.active);

            items.forEach((item) => {
                item.active = true;
            });

            return _instantsearch.addWidgets(items.map((item) => item.widget));
        },
        addAutocomplete(options = {}) {
            const _this = this;

            const querySuggestionsPlugin = createQuerySuggestionsPlugin({
                searchClient: _client,
                indexName: `${_this.indexName()}_query_suggestions`,
                transformSource: (params) => ({
                    onSelect({ state }) {
                        _this.setUiState({
                            query: state.query,
                        });
                    },
                    ...params.source,
                }),
            });

            _autocomplete = autocomplete({
                container: "#autocomplete",
                plugins: [querySuggestionsPlugin],
                detachedMediaQuery: "none",
                openOnFocus: true,
                insights: true,
                classNames: {
                    form: "!rounded-md !bg-white !border-black/10 focus-within:!border-black",
                },
                initialState: {
                    query: _instantsearch.helper?.state.query || "",
                },
                onSubmit({ state }) {
                    _this.setUiState({
                        query: state.query,
                    });
                },
                onReset() {
                    _this.setUiState({
                        query: "",
                    });
                },
                onStateChange({ prevState, state }) {
                    if (prevState.query === state.query) {
                        return;
                    }

                    if (!state.query) {
                        _this.setUiState({
                            query: state.query,
                        });
                    }
                },
                ...options,
            });

            window.addEventListener("popstate", () => {
                _autocomplete.setQuery(_instantsearch.helper?.state.query || "");
            });
        },
        start() {
            _instantsearch.start();
        },
        setUiState(indexUiState) {
            const _this = this;

            const indexName = _this.indexName();

            _instantsearch.setUiState((uiState) => ({
                ...uiState,
                [indexName]: {
                    ...uiState[indexName],
                    // We reset the page when the search state changes.
                    page: 1,
                    ...indexUiState,
                },
            }));
        },
        trans(key, fallback = true) {
            const value = get(this.translations, key);

            if (value) {
                return value;
            }

            if (fallback === true) {
                return key;
            }

            if (fallback) {
                return fallback;
            }

            return null;
        },
        roundToNearest(value) {
            let factor = 1;

            while (value / factor > 100) {
                factor *= 10;
            }

            return Math.floor(value / factor) * factor;
        },
        convert_money(value) {
            return value * this.forex.rate;
        },
        format_money(value, currency, locale) {
            return new Intl.NumberFormat(locale || this.locale, {
                style: "currency",
                currency: currency || this.forex.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(value);
        },
        useGoogleAnalyticsMiddleware() {
            _instantsearch.use(googleAnalyticsMiddleware);
        },
    };
}
